import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface BoxTruckLoadsState {
    // Whether this account is on the Gluon-managed operator allowlist. null = not yet checked.
    isOperator: boolean | null
    /**
     * Whether the Box Truck Profiles presentation is granted by this account's ROLE — a different question
     * from `isOperator`, and the one half of the grant that is invisible here: an ACCOUNTANT gets Profiles
     * from the company running Box Truck at all, which is the Gluon owner allowlist and never reaches the
     * browser. The per-member checkbox half stays a synchronous read of the application document.
     */
    profilesFromRole: boolean
    /**
     * Whether the company-aware Box Truck DEFAULT applies to this member — an accountant on a box-truck
     * company whose tenant has no stored template and no custom role with an opinion. Separate from the
     * flag above because the sidebar tiers are per MODULE: an owner who grants Driver Management and
     * withholds the Loadboard must keep that, and one flag for both would open what they left out.
     */
    accountantDefault: boolean
    /**
     * Whether this APPLICATION runs Box Truck — not whether the caller is an operator. The Gluon owner
     * allowlist decides it and never reaches the browser, so the server answers it on `/access`.
     *
     * Read by `usePermissions` to know that the built-in Dispatcher role is read-only on Ongoing Loads
     * here. Presentation only: the same question is asked again server-side by
     * `permissionGuard({ boxTruckAware: true })`, which is what actually refuses the write.
     */
    boxTruckCompany: boolean
    /**
     * Whether this account may see the pricing panel in the load drawer — its own per-email Gluon tick,
     * answered by the same `/access` call. Separate from `isOperator` because it is a narrower question:
     * the board is a company's load stream, the panel is a number the desk may act on, and the pilot is a
     * couple of dispatchers whose feedback is the point of shipping it.
     */
    pricing: boolean
}

const initialState: BoxTruckLoadsState = {
    isOperator: null,
    profilesFromRole: false,
    accountantDefault: false,
    boxTruckCompany: false,
    pricing: false,
}

const boxTruckLoadsSlice = createSlice({
    name: 'boxTruckLoads',
    initialState,
    reducers: {
        setIsOperator(state, action: PayloadAction<boolean | null>) {
            state.isOperator = action.payload
        },
        setProfilesFromRole(state, action: PayloadAction<boolean>) {
            state.profilesFromRole = action.payload
        },
        setAccountantDefault(state, action: PayloadAction<boolean>) {
            state.accountantDefault = action.payload
        },
        setBoxTruckCompany(state, action: PayloadAction<boolean>) {
            state.boxTruckCompany = action.payload
        },
        setPricingAccess(state, action: PayloadAction<boolean>) {
            state.pricing = action.payload
        },
    },
})

export const { setIsOperator, setProfilesFromRole, setAccountantDefault, setBoxTruckCompany, setPricingAccess } = boxTruckLoadsSlice.actions
export const boxTruckLoadsReducer = boxTruckLoadsSlice.reducer
